import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { db } from '../firebase.js';
import { ref, set, onValue, remove, update } from "firebase/database";

const saveToLocal = (userId, addresses) => {
  const allAddresses = JSON.parse(localStorage.getItem('addresses') || '{}');
  allAddresses[userId] = addresses;
  localStorage.setItem('addresses', JSON.stringify(allAddresses));
};

const getFromLocal = (userId) => {
  const allAddresses = JSON.parse(localStorage.getItem('addresses') || '{}');
  return allAddresses[userId] || [];
};

export const createAddress = createAsyncThunk(
  "addresses/create",
  async ({ userId, address }, { rejectWithValue }) => {
    const addressId = `addr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const addressData = { ...address, id: addressId, createdAt: Date.now() };
    
    // Save to localStorage first
    const localAddresses = getFromLocal(userId);
    localAddresses.push(addressData);
    saveToLocal(userId, localAddresses);
    
    // Try Firebase if available
    if (db) {
      try {
        const addressRef = ref(db, `addresses/${userId}/${addressId}`);
        await set(addressRef, addressData);
        console.log('✅ Address saved to Firebase');
      } catch (error) {
        console.warn('⚠️ Firebase save failed, using local storage:', error.message);
      }
    } else {
      console.warn('⚠️ Firebase not available, using local storage');
    }
    
    return addressData;
  }
);

export const fetchAddresses = createAsyncThunk(
  "addresses/fetch",
  async (userId, { rejectWithValue }) => {
    // Get from localStorage first
    const localAddresses = getFromLocal(userId);
    
    if (!db) {
      console.warn('⚠️ Firebase not available, using local storage');
      return localAddresses;
    }
    
    try {
      return new Promise((resolve, reject) => {
        const addressesRef = ref(db, `addresses/${userId}`);
        onValue(
          addressesRef,
          (snapshot) => {
            const data = snapshot.val() || {};
            const firebaseAddresses = Object.keys(data).map(key => ({ id: key, ...data[key] }));
            
            // Merge with local addresses (Firebase takes priority)
            const mergedAddresses = [...firebaseAddresses];
            localAddresses.forEach(localAddr => {
              if (!mergedAddresses.find(fbAddr => fbAddr.id === localAddr.id)) {
                mergedAddresses.push(localAddr);
              }
            });
            
            // Update localStorage with merged data
            saveToLocal(userId, mergedAddresses);
            resolve(mergedAddresses);
          },
          (error) => {
            console.warn('⚠️ Firebase fetch failed, using local storage:', error.message);
            resolve(localAddresses);
          }
        );
      });
    } catch (error) {
      console.warn('⚠️ Firebase error, using local storage:', error.message);
      return localAddresses;
    }
  }
);

export const updateAddress = createAsyncThunk(
  "addresses/update",
  async ({ userId, addressId, address }, { rejectWithValue }) => {
    const updatedAddress = { ...address, id: addressId, updatedAt: Date.now() };
    
    // Update localStorage first
    const localAddresses = getFromLocal(userId);
    const index = localAddresses.findIndex(addr => addr.id === addressId);
    if (index !== -1) {
      localAddresses[index] = updatedAddress;
      saveToLocal(userId, localAddresses);
    }
    
    // Try Firebase if available
    if (db) {
      try {
        const addressRef = ref(db, `addresses/${userId}/${addressId}`);
        await update(addressRef, updatedAddress);
        console.log('✅ Address updated in Firebase');
      } catch (error) {
        console.warn('⚠️ Firebase update failed, using local storage:', error.message);
      }
    }
    
    return updatedAddress;
  }
);

export const deleteAddress = createAsyncThunk(
  "addresses/delete",
  async ({ userId, addressId }, { rejectWithValue }) => {
    // Delete from localStorage first
    const localAddresses = getFromLocal(userId);
    const filteredAddresses = localAddresses.filter(addr => addr.id !== addressId);
    saveToLocal(userId, filteredAddresses);
    
    // Try Firebase if available
    if (db) {
      try {
        await remove(ref(db, `addresses/${userId}/${addressId}`));
        console.log('✅ Address deleted from Firebase');
      } catch (error) {
        console.warn('⚠️ Firebase delete failed, using local storage:', error.message);
      }
    }
    
    return addressId;
  }
);

const addressSlice = createSlice({
  name: "addresses",
  initialState: {
    items: [],
    selectedAddress: null,
    status: "idle",
    error: null
  },
  reducers: {
    selectAddress: (state, action) => {
      state.selectedAddress = action.payload;
    },
    clearSelectedAddress: (state) => {
      state.selectedAddress = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createAddress.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.status = "succeeded";
      })
      .addCase(createAddress.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createAddress.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(fetchAddresses.fulfilled, (state, action) => {
        state.items = action.payload;
        state.status = "succeeded";
      })
      .addCase(fetchAddresses.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAddresses.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(updateAddress.fulfilled, (state, action) => {
        const index = state.items.findIndex(addr => addr.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        state.status = "succeeded";
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.items = state.items.filter(addr => addr.id !== action.payload);
        state.status = "succeeded";
      });
  }
});

export const { selectAddress, clearSelectedAddress } = addressSlice.actions;
export default addressSlice.reducer;